// Live preview of everything a new customer receives, per path, in order — WhatsApp messages
// as chat bubbles, emails as email cards, each with when it's sent. Rendered from the flow
// being edited in FlowEditor (unsaved edits show immediately), with sample values.
import {FC, useState} from 'react'
import {Flow} from './_requests'

type Path = 'whatsapp' | 'website' | 'lead_form'

const SAMPLE = {
  first_name: 'דני',
  email: 'dani@example.com',
  password: '48213907',
  login_url: 'https://api.fbzipper.com/r/Xk29fQ',
}

const fill = (t: string, vars: Record<string, string | number>) =>
  String(t || '').replace(/\{(\w+)\}/g, (all, k) => (vars[k] != null ? String(vars[k]) : all))

const whenLabel = (h: number) => {
  if (h < 24) return `${h} שעות אחרי ההרשמה`
  const d = Math.round((h / 24) * 10) / 10
  return d === 1 ? 'יום אחרי ההרשמה' : `${d} ימים אחרי ההרשמה`
}

// WhatsApp buttons under a message (reply buttons, or a link button with its ↗).
const Buttons: FC<{items?: string[]}> = ({items}) =>
  items && items.length ? (
    <div style={{borderTop: '1px solid #eef0f3', marginTop: 6}}>
      {items.map((b) => (
        <div
          key={b}
          style={{
            textAlign: 'center',
            color: '#0284c7',
            fontSize: 13,
            fontWeight: 600,
            padding: '6px 0 2px',
            borderTop: '1px solid #f3f4f6',
          }}
        >
          {b}
        </div>
      ))}
    </div>
  ) : null

// The bot keeps the link in the text (some WhatsApp clients don't show link buttons) and
// adds it if the text doesn't have it.
const noLink = (text: string, url: string) => (!url || text.includes(url) ? text : text + '\n' + url)

const Bubble: FC<{from: 'them' | 'us'; text: string; sender?: string; buttons?: string[]}> = ({
  from,
  text,
  sender,
  buttons,
}) => (
  <div className={`d-flex mb-2 ${from === 'us' ? 'justify-content-start' : 'justify-content-end'}`}>
    <div
      style={{
        maxWidth: '82%',
        whiteSpace: 'pre-wrap',
        background: from === 'us' ? '#ffffff' : '#d9fdd3',
        borderRadius: 10,
        padding: '7px 11px',
        boxShadow: '0 1px 1px rgba(0,0,0,.1)',
        fontSize: 13,
      }}
    >
      {sender && <div style={{fontSize: 11, color: '#16a34a', fontWeight: 700}}>{sender}</div>}
      {text}
      <Buttons items={buttons} />
    </div>
  </div>
)

const VideoBubble: FC<{url: string; caption: string; buttons?: string[]}> = ({
  url,
  caption,
  buttons,
}) => (
  <div className='d-flex mb-2 justify-content-start'>
    <div
      style={{
        maxWidth: '82%',
        background: '#fff',
        borderRadius: 10,
        padding: 5,
        boxShadow: '0 1px 1px rgba(0,0,0,.1)',
      }}
    >
      <video
        src={url}
        controls
        preload='metadata'
        style={{
          width: '100%',
          maxHeight: 220,
          borderRadius: 8,
          background: '#000',
          display: 'block',
        }}
      />
      {caption && (
        <div style={{whiteSpace: 'pre-wrap', fontSize: 13, padding: '6px 6px 2px'}}>{caption}</div>
      )}
      <Buttons items={buttons} />
    </div>
  </div>
)

const CONNECT_EX = 'https://console.fbzipper.com/c/8f3a…'

const Step: FC<{when: string; title: string; children?: React.ReactNode; muted?: boolean}> = ({
  when,
  title,
  children,
  muted,
}) => (
  <div className='d-flex gap-3 mb-4' style={{opacity: muted ? 0.5 : 1}}>
    <div style={{flex: '0 0 92px'}} className='text-end'>
      <span className='badge badge-light-primary fs-9' style={{whiteSpace: 'normal'}}>
        {when}
      </span>
    </div>
    <div
      className='flex-grow-1'
      style={{borderInlineStart: '2px solid #e2e8f0', paddingInlineStart: 14}}
    >
      <div className='fw-bold fs-7 mb-2'>{title}</div>
      {children}
    </div>
  </div>
)

const Chat: FC<{children: React.ReactNode}> = ({children}) => (
  <div style={{background: '#efeae2', borderRadius: 12, padding: 12, maxWidth: 440}}>
    {children}
  </div>
)

const EmailCard: FC<{subject: string; title: string; body: string; cta: string}> = ({
  subject,
  title,
  body,
  cta,
}) => (
  <div
    style={{
      maxWidth: 440,
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      overflow: 'hidden',
      background: '#fff',
    }}
  >
    <div
      style={{background: '#f8fafc', padding: '8px 12px', fontSize: 12}}
      className='border-bottom'
    >
      <div className='text-muted'>מאת: FBZipper</div>
      <div>
        <b>נושא:</b> {subject}
      </div>
    </div>
    <div style={{padding: 14}}>
      <div className='fw-bold fs-6 mb-2'>{title}</div>
      <div className='fs-7 mb-3' style={{whiteSpace: 'pre-line', lineHeight: 1.6}}>
        {body}
      </div>
      <span className='btn btn-sm btn-primary' style={{borderRadius: 999, pointerEvents: 'none'}}>
        {cta}
      </span>
      <div className='text-muted mt-3' style={{fontSize: 10}}>
        קיבלתם את המייל כי נרשמתם ל-FBZipper · הסרה מרשימת התפוצה
      </div>
    </div>
  </div>
)

const FlowPreview: FC<{flow: Flow; welcome: string}> = ({flow, welcome}) => {
  const [path, setPath] = useState<Path>('whatsapp')
  const vars = {...SAMPLE, trial_days: flow.trial_days}
  const bot = flow.whatsapp_bot
  const welcomeText = fill(welcome, vars)
  const emails = flow.emails.steps.map((s, i) => ({...s, n: i + 1})).filter((s) => s.enabled)

  const emailSteps = (note: string) =>
    !flow.emails.enabled ? (
      <Step when='—' title='מיילי תזכורת כבויים' muted />
    ) : (
      emails.map((s) => (
        <Step key={s.n} when={whenLabel(s.after_hours)} title={`מייל ${s.n} — ${note}`}>
          <EmailCard
            subject={fill(s.subject, vars)}
            title={fill(s.title, vars)}
            body={fill(s.body, vars)}
            cta={s.cta}
          />
        </Step>
      ))
    )

  return (
    <div>
      <div className='btn-group btn-group-sm mb-4'>
        {(
          [
            ['whatsapp', '💬 מודעת וואטסאפ'],
            ['website', '🌐 דף הרשמה'],
            ['lead_form', '📝 טופס פייסבוק'],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            className={`btn ${path === k ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setPath(k)}
          >
            {l}
          </button>
        ))}
      </div>

      {path === 'whatsapp' && (
        <>
          <Step when='לחיצה' title='הלקוח לוחץ "שליחת הודעה" במודעה — נפתח צ׳אט עם 054-615-5952'>
            <Chat>
              <Bubble from='them' text='היי, אשמח לשמוע על FBZipper' />
            </Chat>
          </Step>
          {!bot.enabled ? (
            <Step
              when='—'
              title='הבוט כבוי — ההודעה רק נכנסת ללשונית וואטסאפ ונציג עונה ידנית'
              muted
            />
          ) : (
            <>
              <Step when='מיד' title='הבוט עונה ושואל לשם'>
                <Chat>
                  <Bubble from='us' sender='FBZipper' text={fill(bot.greeting, vars)} />
                  <Bubble from='them' text='דני כהן' />
                </Chat>
              </Step>
              <Step when='מיד' title='שואל מה הוא מפרסם'>
                <Chat>
                  <Bubble
                    from='us'
                    sender='FBZipper'
                    text={fill(bot.ask_business, vars)}
                    buttons={['🏠 נדל״ן', '🚗 רכב', '✏️ משהו אחר']}
                  />
                  <Bubble from='them' text='🏠 נדל״ן' />
                </Chat>
              </Step>
              {bot.ask_cities && (
                <Step when='רק לנדל״ן' title='נדל״ן (כפתור, או AI מזהה מטקסט חופשי) — שואל באילו ערים'>
                  <Chat>
                    <Bubble from='us' sender='FBZipper' text={fill(bot.ask_cities, vars)} />
                    <Bubble from='them' text='ת״א, רמת גן וגבעתיים' />
                  </Chat>
                </Step>
              )}
              <Step when='מיד' title={`נפתח חשבון ניסיון ל-${flow.trial_days} ימים (בלי פרטי כניסה עדיין)`}>
                <Chat>
                  <Bubble from='us' sender='FBZipper' text={fill(bot.created, vars)} />
                  {bot.campaigns_created && (
                    <Bubble
                      from='us'
                      sender='FBZipper'
                      text={fill(bot.campaigns_created, {
                        ...vars,
                        count: 3,
                        campaigns: 'תל אביב, רמת גן, גבעתיים',
                      })}
                    />
                  )}
                </Chat>
              </Step>
              {(bot.tutorial_video || bot.tutorial_caption) && (
                <Step when={`אחרי ${flow.whatsapp_bot.step_delay_sec} שנ׳`} title='כניסה לחשבון בלחיצה אחת — שם מחכה המדריך (קמפיין → פוסט → פייסבוק)'>
                  <Chat>
                    {bot.tutorial_video ? (
                      <VideoBubble
                        url={bot.tutorial_video}
                        caption={fill(bot.tutorial_caption, vars)}
                      />
                    ) : (
                      <Bubble
                        from='us'
                        sender='FBZipper'
                        text={noLink(
                          fill(bot.tutorial_caption, vars),
                          (fill(bot.tutorial_caption, vars).match(/https:\/\/\S+/) || [''])[0]
                        )}
                        buttons={['🔗 כניסה לחשבון ↗']}
                      />
                    )}
                  </Chat>
                </Step>
              )}
              {bot.connect_step_enabled && (
              <Step when={`אחרי ${flow.whatsapp_bot.step_delay_sec * 3} שנ׳`} title='צעד 2: חיבור פרופיל הפייסבוק — סרטון + קישור + הוראות'>
                <Chat>
                  {bot.connect_video ? (
                    <VideoBubble
                      url={bot.connect_video}
                      caption={noLink(
                        fill(bot.connect_caption, {...vars, connect_url: CONNECT_EX}),
                        CONNECT_EX
                      )}
                      buttons={['🔑 חיבור הפייסבוק ↗']}
                    />
                  ) : (
                    <Bubble
                      from='us'
                      sender='FBZipper'
                      text={noLink(
                        fill(bot.connect_caption, {...vars, connect_url: CONNECT_EX}),
                        CONNECT_EX
                      )}
                      buttons={['🔑 חיבור הפייסבוק ↗']}
                    />
                  )}
                  <Bubble
                    from='us'
                    sender='FBZipper'
                    text='חיברת? לחיצה כאן ואבדוק שהכול תקין 👇'
                    buttons={['✅ חיברתי', '🙋 צריך עזרה']}
                  />
                  <Bubble from='them' text='✅ חיברתי' />
                </Chat>
              </Step>
              )}
              <Step
                when='כשהפרופיל מתחבר (בדיקה כל 2 דק׳, או בלחיצה על ✅ חיברתי)'
                title='רק עכשיו: פרטי הכניסה למערכת (סיסמה חדשה)'
              >
                <Chat>
                  <Bubble
                    from='us'
                    sender='FBZipper'
                    text={noLink(fill(bot.logged_in || '', vars), vars.login_url)}
                    buttons={['🔗 כניסה למערכת ↗']}
                  />
                  <div className='text-muted fs-8 text-center'>
                    לא מחובר עדיין? הבוט מבקש לנסות שוב או לבקש עזרה; "צריך עזרה" מעביר לנציג
                  </div>
                </Chat>
              </Step>
              <Step
                when='אחר כך'
                title='כל הודעה נוספת — לשונית וואטסאפ, נציג עונה (הבוט לא עונה יותר)'
                muted
              />
              <Step
                when='—'
                title='מיילי תזכורת: לא נשלחים — בהרשמה בוואטסאפ אין כתובת מייל'
                muted
              />
              <Step
                when='—'
                title='מי שכבר לקוח (ולא מספר בדיקה) מקבל רק את ההודעה הזו, פעם ביום לכל היותר:'
              >
                <Chat>
                  <Bubble from='us' sender='FBZipper' text={fill(bot.existing, vars)} />
                </Chat>
              </Step>
            </>
          )}
        </>
      )}

      {path === 'website' && (
        <>
          <Step when='לחיצה' title='הלקוח לוחץ "הרשמה" במודעה → טופס ההרשמה'>
            <div className='fs-8 text-muted'>
              גוגל / פייסבוק בלחיצה (ואז מסך שמבקש טלפון), או שם + טלפון.{' '}
              {flow.default_link.replace('https://', '')}
            </div>
          </Step>
          <Step
            when='מיד'
            title='וואטסאפ ברוכים הבאים (ממספר התמיכה) — בגוגל/פייסבוק: אחרי שהזין טלפון'
          >
            <Chat>
              <Bubble from='us' sender='FBZipper תמיכה' text={welcomeText} />
            </Chat>
          </Step>
          {emailSteps('נשלח רק אם עוד לא חיבר פרופיל')}
        </>
      )}

      {path === 'lead_form' && (
        <>
          <Step
            when='לחיצה'
            title='הלקוח לוחץ "הרשמה" → טופס בתוך פייסבוק (שם, טלפון, אימייל ממולאים)'
          >
            <div
              style={{
                maxWidth: 440,
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: 14,
                background: '#fff',
              }}
            >
              <div className='fw-bold mb-2'>{flow.lead_form.intro_title}</div>
              <ul className='fs-8 ps-4 mb-3'>
                {flow.lead_form.intro_items.filter(Boolean).map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
              <span className='btn btn-sm btn-primary w-100' style={{pointerEvents: 'none'}}>
                {flow.lead_form.intro_button}
              </span>
            </div>
          </Step>
          <Step when='אחרי שליחה' title='מסך התודה בפייסבוק'>
            <div
              style={{
                maxWidth: 440,
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: 14,
                background: '#fff',
              }}
            >
              <div className='fw-bold mb-1'>{flow.lead_form.thanks_title}</div>
              <div className='fs-8 mb-3'>{flow.lead_form.thanks_body}</div>
              <span className='btn btn-sm btn-light-primary w-100' style={{pointerEvents: 'none'}}>
                {flow.lead_form.thanks_button}
              </span>
            </div>
          </Step>
          <Step
            when='תוך ~3 דקות'
            title='נפתח חשבון אוטומטית + וואטסאפ ברוכים הבאים (ממספר התמיכה)'
          >
            <Chat>
              <Bubble from='us' sender='FBZipper תמיכה' text={welcomeText} />
            </Chat>
          </Step>
          {emailSteps('אם השאיר אימייל ועוד לא חיבר פרופיל')}
        </>
      )}
    </div>
  )
}

export default FlowPreview
